import _ from 'underscore';
import type { Flight, ServerContextType } from './index.d';

type AllocationField = 'AccountFlightAllocations' | 'CustomerFlightAllocations';

interface ServerContextParseParams {
  serverContext: ServerContextType;
  allFlightActivity: { trace: (msg: unknown) => void };
}

interface JsonParseParams {
  flightsJson: string | null | undefined;
  allFlightActivity: { trace: (msg: unknown) => void };
}

/**
 * Creates a memoized parser that reads a flight-allocations JSON string from
 * ServerContext and returns the parsed `Flight[]`. Each call site gets its own
 * memo cache keyed on the raw JSON string for that field.
 */
export const createServerContextFlightParser = (field: AllocationField) =>
  _.memoize(
    ({ serverContext, allFlightActivity }: ServerContextParseParams): Flight[] | undefined => {
      const flightsJson = serverContext?.[field];
      allFlightActivity.trace({ flightsJson });

      if (!flightsJson) {
        return undefined;
      }

      try {
        return JSON.parse(flightsJson) as Flight[];
      } catch {
        allFlightActivity.trace(`Failed to parse ${field} JSON: ${flightsJson}`);
        return undefined;
      }
    },
    ({ serverContext }: ServerContextParseParams) => `${serverContext?.[field]}`
  );

/**
 * Memoized parser that takes a raw flight-allocations JSON string (without
 * requiring the full ServerContext). Use this from non-hook callers that only
 * need to check flight status and don't want to depend on the ServerContext shape.
 */
export const parseFlightsJson = _.memoize(
  ({ flightsJson, allFlightActivity }: JsonParseParams): Flight[] | undefined => {
    allFlightActivity.trace({ flightsJson });

    if (!flightsJson) {
      return undefined;
    }

    try {
      return JSON.parse(flightsJson) as Flight[];
    } catch {
      allFlightActivity.trace(`Failed to parse flights JSON: ${flightsJson}`);
      return undefined;
    }
  },
  ({ flightsJson }: JsonParseParams) => `${flightsJson}`
);
